import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {prefetch} from "services/query-client";
import {CubeTransparentIcon} from "@heroicons/react/outline";
import {formattedDateTime} from "services/date";
import Button from "components/Button";
import {PlusIcon} from "@heroicons/react/solid";
import {truncate} from "services/string";
import {Zone} from "types/Zone";
import {useNotifications} from "context/NotificationContext";
import {useNavigate} from "react-router-dom";
import DeleteZone from '../Delete';
import {useOperator} from "context/OperatorContext";
import BooleanIndicator from "../../../components/BooleanIndicator";
import {useAuth} from "../../../context/AuthContext";
import NotAuthorised from "../../NotAuthorised";
import ZoneContextMenu from "../../../components/menus/ZoneContextMenu";

// Component ----

function ZoneList() {
  const {termFor} = useOperator();
  const auth = useAuth();
  const {notify} = useNotifications();
  const navigate = useNavigate();

  const [deletingZone, setDeletingZone] = useState<Zone | null>(null);
  const [page, setPage] = useState<number>(1);

  const {data, isLoading} = useQuery(
    ['zones.all', {page}],
    () => ngage.zones.all({page}),
    {keepPreviousData: true, enabled: auth.can('zones:read')}
  )

  // Prevent unauthorised viewing
  if (auth.cannot('zones:read')) {
    return <NotAuthorised/>
  }

  // Prefetch next and previous pages
  prefetch('zones.all', {page}, ngage.zones.all)

  // Prepare page actions and breadcrumbs
  const actions = (
    <>
      {auth.can('zones:write') &&
        <Button icon={PlusIcon} onClick={() => navigate('/zones/new')}>New</Button>
      }
    </>
  )

  const breadcrumbs = {
    'Operations': '/',
    [termFor('Zones')]: '/zones'
  }

  return (
    <Page
      title={termFor('Zones')}
      actions={actions}
      breadcrumbs={breadcrumbs}>
      <Table paginationData={data} setPage={setPage}>
        <TableHead>
          <TableRow>
            <TableHeader>Name</TableHeader>
            <TableHeader>Description</TableHeader>
            <TableHeader>{termFor('Site')}</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Exhibits')}</TableHeader>
            <TableHeader alignment={TableHeader.alignments.RIGHT}>{termFor('Facilities')}</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Restricted</TableHeader>
            <TableHeader alignment={TableHeader.alignments.CENTER}>Visible</TableHeader>
            <TableHeader>Added</TableHeader>
            <TableHeader/>
          </TableRow>
        </TableHead>

        <TableBody isLoading={isLoading}>

          {data && data.data.length < 1 ?
            <TableRow index={0}>
              <TableData colSpan={8}>
                <div className="py-4 flex flex-col justify-center items-center">
                  <CubeTransparentIcon className="h-8 w-8 text-gray-400 mb-2"/>
                  <span className="text-sm font-medium text-gray-600">No {termFor('zones')}</span>
                </div>
              </TableData>
            </TableRow>
            : data?.data.map((zone, index) => (
              <TableRow key={zone.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      <ZoneContextMenu
                        zone={zone}
                        onDeleteZone={() => setDeletingZone(zone)}
                        deleteDisabled={!zone.is_empty}
                        position="left"
                      />
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      <CubeTransparentIcon className="h-5 w-5 mr-2 text-gray-500"/> {zone.name}
                    </div>
                  </div>
                </TableData>
                <TableData>{truncate(zone.description, 100)}</TableData>
                <TableData>{zone.site.name}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{zone.exhibit_count}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>{zone.facility_count}</TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={zone.is_restricted}/>
                </TableData>
                <TableData><BooleanIndicator value={!zone.is_hidden}/></TableData>
                <TableData>{formattedDateTime(zone.created_at)}</TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  <ZoneContextMenu
                    zone={zone}
                    onDeleteZone={() => setDeletingZone(zone)}
                    deleteDisabled={!zone.is_empty}
                  />
                </TableData>

                {/* Delete confirmation modal */}
                <DeleteZone
                  zone={deletingZone}
                  show={zone === deletingZone}
                  onCancel={() => setDeletingZone(null)}
                  onDelete={zone => {
                    navigate('/zones')
                    notify("success", `${termFor('Zone')} Deleted`, `${zone.name} has been permanently removed`)
                  }}
                  onDeleteFail={zone => {
                    setDeletingZone(null)
                    notify("error", `${termFor('Zone')} Not Deleted`, `${zone.name} could not be removed`)
                  }}
                />
              </TableRow>
            ))}

        </TableBody>
      </Table>
    </Page>
  );
}

export default ZoneList;